// To update the data displayed by this component:
// `PLAID_CLIENT_ID=<client_id> PLAID_SECRET=<secret> make coverage-csv`

import React, { useState, useMemo } from 'react';
import Select from '../../Inputs/Select/Select';
import ThreadsTableGroup from 'plaid-threads/TableGroup';
import { Box } from 'src/components-v2/Layout';
import styles from './index.module.scss';
import ThreadsSearchInput from 'threads5/SearchInput';
import Checkbox from '../../Inputs/Checkbox/Checkbox';
import Tooltip from 'plaid-threads/Tooltip';

interface Institution {
  institution_id: string;
  name: string;
  country: string;
  assets: number;
  auth: number;
  balance: number;
  cra_base_report: number;
  cra_cashflow_insights: number;
  cra_income_insights: number;
  cra_monitoring: number;
  cra_network_insights: number;
  identity: number;
  identity_match: number;
  income_verification: number;
  investments: number;
  investments_auth: number;
  liabilities: number;
  protect_linked_bank: number;
  signal: number;
  statements: number;
  transactions: number;
  transfer: number;
}

const PRODUCT_OPTIONS = [
  { label: 'Assets', value: 'assets' },
  { label: 'Auth (Instant)', value: 'auth' },
  { label: 'Balance', value: 'balance' },
  { label: 'Consumer Report Base Report', value: 'cra_base_report' },
  {
    label: 'Consumer Report Cashflow Insights',
    value: 'cra_cashflow_insights',
  },
  { label: 'Consumer Report Income Insights', value: 'cra_income_insights' },
  { label: 'Consumer Report Monitoring', value: 'cra_monitoring' },
  { label: 'Consumer Report Network Insights', value: 'cra_network_insights' },
  { label: 'Identity', value: 'identity' },
  { label: 'Identity Match', value: 'identity_match' },
  { label: 'Income', value: 'income_verification' },
  { label: 'Investments', value: 'investments' },
  { label: 'Investments Move', value: 'investments_auth' },
  { label: 'Liabilities', value: 'liabilities' },
  { label: 'Protect', value: 'protect_linked_bank' },
  { label: 'Signal Transaction Scores', value: 'signal' },
  { label: 'Statements', value: 'statements' },
  { label: 'Transactions', value: 'transactions' },
  { label: 'Transfer', value: 'transfer' },
];

const parseCSVRow = (row: string): string[] => {
  const values: string[] = [];
  let current = '';
  let inQuotes = false;

  // A little bit o' work to not include quotes in the values
  for (let i = 0; i < row.length; i++) {
    const char = row[i];

    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      values.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }

  // Add the last value
  values.push(current.trim());

  return values;
};

const parseCSV = (csv: string): Institution[] => {
  const lines = csv.trim().split('\n');
  // Skip comment lines and header
  const dataRows = lines.filter((line) => !line.startsWith('#')).slice(1);

  const institutionsList = dataRows.map((row) => {
    const values = parseCSVRow(row);
    const [
      institution_id,
      name,
      country,
      assets,
      auth,
      balance,
      cra_base_report,
      cra_cashflow_insights,
      cra_income_insights,
      cra_monitoring,
      cra_network_insights,
      identity,
      identity_match,
      income_verification,
      investments,
      investments_auth,
      liabilities,
      protect_linked_bank,
      signal,
      statements,
      transactions,
      transfer,
    ] = values;

    return {
      institution_id,
      name,
      country,
      assets: Number(assets),
      auth: Number(auth),
      balance: Number(balance),
      cra_base_report: Number(cra_base_report),
      cra_cashflow_insights: Number(cra_cashflow_insights),
      cra_income_insights: Number(cra_income_insights),
      cra_monitoring: Number(cra_monitoring),
      cra_network_insights: Number(cra_network_insights),
      identity: Number(identity),
      identity_match: Number(identity_match),
      income_verification: Number(income_verification),
      investments: Number(investments),
      investments_auth: Number(investments_auth),
      liabilities: Number(liabilities),
      protect_linked_bank: Number(protect_linked_bank),
      signal: Number(signal),
      statements: Number(statements),
      transactions: Number(transactions),
      transfer: Number(transfer),
    } as Institution;
  });

  return institutionsList;
};

interface SelectOption {
  label: string;
  value: string;
}

const USCoverageExplorer: React.FC = () => {
  const [csvData, setCsvData] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [tableColumns, setTableColumns] = useState<string[]>([
    'auth',
    'balance',
    'transactions',
  ]);
  const [filterOption, setFilterOption] = useState<string>('all');
  const [useProductFilter, setUseProductFilter] = useState<boolean>(false);
  const [csvFileDate, setCsvFileDate] = useState<string>('');
  const [selectedCountry, setSelectedCountry] = useState<string>('US');
  // Fetch CSV data on component mount
  React.useEffect(() => {
    const prefix = window.location.pathname.split('/docs')[0];
    fetch(`${prefix}/documents/us_institution_coverage.csv`)
      .then((response) => response.text())
      .then((data) => {
        // Extract timestamp from CSV comment lines
        const lines = data.split('\n');
        const timestampLine = lines.find((line) =>
          line.startsWith('# Generated on:'),
        );
        if (timestampLine) {
          const timestamp = timestampLine
            .replace('# Generated on: ', '')
            .trim();
          const date = new Date(timestamp);
          setCsvFileDate(
            date.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            }),
          );
        }

        setCsvData(data);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('Error loading CSV data:', error);
        setIsLoading(false);
      });
  }, []);

  const banks = useMemo(() => (csvData ? parseCSV(csvData) : []), [csvData]);

  // To consider: Should we cache our "filtered by product" results and then
  // have a separate "filtered by search" results?
  const filteredBanks = useMemo(() => {
    let filtered = banks;

    // Filter by country
    if (selectedCountry) {
      filtered = filtered.filter((bank) => bank.country === selectedCountry);
    }

    // Filter by product support if enabled
    if (useProductFilter && tableColumns.length > 0) {
      filtered = filtered.filter((bank) => {
        if (filterOption === 'all') {
          return tableColumns.every(
            (col) => bank[col as keyof Institution] === 1,
          );
        } else if (filterOption === 'any') {
          return tableColumns.some(
            (col) => bank[col as keyof Institution] === 1,
          );
        } else if (filterOption === 'none') {
          return tableColumns.every(
            (col) => bank[col as keyof Institution] === 0,
          );
        }
        return true;
      });
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((bank) =>
        bank.name.toLowerCase().includes(query),
      );
    }

    return filtered;
  }, [
    banks,
    searchQuery,
    useProductFilter,
    filterOption,
    tableColumns,
    selectedCountry,
  ]);

  const columns = useMemo(
    () => [
      {
        Header: 'Institution',
        accessor: 'bank_name',
        setWidth: '400px',
        sortType: (rowA, rowB) => {
          const a = rowA.original.name.toLowerCase().trim();
          const b = rowB.original.name.toLowerCase().trim();
          return a.localeCompare(b, undefined, { sensitivity: 'base' });
        },
      },
      ...PRODUCT_OPTIONS.filter((product) =>
        tableColumns.includes(product.value),
      ).map((product) => ({
        Header:
          product.value === 'auth' ? (
            <Tooltip
              placement='top'
              showOn='hover'
              noDelay={true}
              className={styles.tooltip}
              target={<span style={{ whiteSpace: 'nowrap' }}>Auth *</span>}
            >
              <div className={styles.productCardWrapper}>
                <a className={styles.card}>
                  <div className={styles.text}>
                    <p className={styles.productCardSubtitle}>
                      Coverage for Auth reflects coverage for instant,
                      credential-based connections only. A bank shown as not
                      supporting Auth can still support other verification
                      methods, such as micro-deposits
                    </p>
                  </div>
                </a>
              </div>
            </Tooltip>
          ) : product.value === 'transfer' ? (
            <Tooltip
              placement='top'
              showOn='hover'
              noDelay={true}
              className={styles.tooltip}
              target={
                <span
                  style={{
                    whiteSpace: 'nowrap',
                  }}
                >
                  Transfer *
                </span>
              }
            >
              <div className={styles.productCardWrapper}>
                <a className={styles.card}>
                  <div className={styles.text}>
                    <p>
                      Coverage for Transfer reflects coverage for instant,
                      credential-based connections only. A bank shown as not
                      supporting Transfer can still support other verification
                      methods, such as micro-deposits
                    </p>
                  </div>
                </a>
              </div>
            </Tooltip>
          ) : product.value === 'investments_auth' ? (
            <Tooltip
              placement='top'
              showOn='hover'
              noDelay={true}
              className={styles.tooltip}
              target={
                <span style={{ whiteSpace: 'nowrap' }}>Investments Move *</span>
              }
            >
              <div className={styles.productCardWrapper}>
                <a className={styles.card}>
                  <div className={styles.text}>
                    <p className={styles.productCardSubtitle}>
                      Coverage for Investments Move reflects coverage for the
                      default Investments Move flow only. A bank shown as not
                      supporting Investments Move can still support Investments
                      Move via fallback flows
                    </p>
                  </div>
                </a>
              </div>
            </Tooltip>
          ) : (
            <span style={{ transform: 'translateY(1px)' }}>
              {product.label}
            </span>
          ),

        accessor: product.value,
        setWidth: '80px',
        align: 'center',
        sortType: 'alphanumeric',
      })),
    ],
    [tableColumns],
  );

  const rows = useMemo(
    () =>
      filteredBanks.map((bank) => {
        // Generate product support indicators dynamically
        const productSupport = Object.fromEntries(
          PRODUCT_OPTIONS.map((product) => [
            product.value,
            bank[product.value as keyof Institution] === 1 ? '✓' : '-',
          ]),
        );

        return {
          ...bank,
          ...productSupport,
          bank_name: (
            <Box display='flex' alignItems='center'>
              <b>{bank.name}</b>
            </Box>
          ),
        };
      }),
    [filteredBanks],
  );

  return (
    <div className={styles.container}>
      <Box className={styles.filterGrid}>
        <Box>
          <Box color='secondary' marginBottom={2} fontWeight={600}>
            Select Products
          </Box>
          <Box marginBottom={0}>
            <Select
              id='columnSelector'
              label=''
              name='columnSelector'
              isMulti={true}
              placeholder='Choose columns to display...'
              value={
                tableColumns
                  .map((col) => {
                    const option = PRODUCT_OPTIONS.find((p) => p.value === col);
                    return option
                      ? { label: option.label, value: option.value }
                      : null;
                  })
                  .filter(Boolean) as any
              }
              onChange={(selectedOptions: SelectOption[]) => {
                if (Array.isArray(selectedOptions)) {
                  setTableColumns(
                    selectedOptions.map((option: SelectOption) => option.value),
                  );
                }
              }}
              options={PRODUCT_OPTIONS}
            />
          </Box>

          <Box
            style={{ color: useProductFilter ? 'inherit' : 'gray' }}
            className={styles.filterRow}
          >
            <Checkbox
              id='useProductFilter'
              value={useProductFilter}
              onChange={(e) => {
                setUseProductFilter(e.target.checked);
              }}
              className={styles.checkbox}
            ></Checkbox>

            <span
              style={{
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              Only show institutions that support
            </span>
            <Box minWidth='8rem' flexShrink={0} style={{ marginBottom: '0px' }}>
              <Select
                size={40}
                id='filterOptionSelect'
                label=''
                name='filterOptionSelect'
                value={
                  {
                    label: filterOption,
                    value: filterOption,
                  } as any
                }
                onChange={(option: any) => {
                  setFilterOption(option.value);
                }}
                options={[
                  { label: 'all', value: 'all' },
                  { label: 'any', value: 'any' },
                ]}
                disabled={!useProductFilter}
              />
            </Box>
            <span style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
              of these products
            </span>
          </Box>
        </Box>

        <Box width='100%'>
          <Box color='secondary' marginBottom={2} fontWeight={600}>
            Select Country
          </Box>
          <Box width='100%' style={{ marginBottom: '0px' }}>
            <Select
              id='countryFilter'
              label=''
              name='countrySelect'
              onChange={(e) => {
                setSelectedCountry(e.value);
                setSearchQuery('');
              }}
              options={[
                { label: '\u{1F1FA}\u{1F1F8} United States (US)', value: 'US' },
                { label: '\u{1F1E8}\u{1F1E6} Canada (CA)', value: 'CA' },
              ]}
              defaultValue={{
                label: '\u{1F1FA}\u{1F1F8} United States (US)',
                value: 'US',
              }}
            />
          </Box>
        </Box>
      </Box>

      {(tableColumns.includes('transfer') || tableColumns.includes('auth')) &&
        false && (
          <Box
            marginBottom={0}
            padding={2}
            style={{
              backgroundColor: '#f8f9fa',
              borderRadius: '4px',
              border: '1px solid #e9ecef',
            }}
          >
            <div style={{ fontSize: '1.2rem', color: '#4c5359' }}>
              Note: Coverage for Auth and Transfer reflects coverage for
              instant, credential-based connections only. A bank shown as not
              supporting Auth or Transfer can still support other verification
              methods, such as micro-deposits.{' '}
            </div>
          </Box>
        )}

      <ThreadsTableGroup
        isLoading={isLoading}
        columns={columns}
        className={styles.table}
        rows={rows}
        label=''
        hasPagination
        pagination={{
          rowsPerPage: 50,
        }}
        initialState={{
          sortBy: [
            {
              id: 'bank_name',
              desc: false,
            },
          ],
        }}
        search={
          <ThreadsSearchInput
            className='search-input'
            classes={{}}
            state='default'
            id='bank-search'
            label='Search institutions'
            onChange={(e) => setSearchQuery(e.target.value)}
            onClearButtonClick={() => setSearchQuery('')}
            placeholder='Search institutions by name'
            value={searchQuery}
            inputProps={{ spellCheck: false }}
            size='48'
            sx={{
              fontSize: '1.6rem',
              margin: '0',
              padding: '0',
              '& input': {
                border: 'none',
                padding: '0',
                margin: '0',
                '&:focus': {
                  outline: 'none',
                  border: 'none',
                  boxShadow: 'none',
                },
              },
            }}
          />
        }
      />
      <Box marginBottom={2} marginTop={2}>
        <Box
          color='secondary'
          marginBottom={1}
          marginTop={2}
          fontWeight={400}
          fontSize='1.2rem'
        >
          Last updated: {csvFileDate}
        </Box>
      </Box>
    </div>
  );
};

export default USCoverageExplorer;
